import React, { useEffect, useCallback, useState, useRef } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Button, Row, Col, Container } from 'react-bootstrap';
import { addMinutes, parseISO, format } from 'date-fns';

import { useToast } from '../../hooks/toast';

import api from '../../services/api';

import SelectSync, { IOption } from '../SelectSync';
import Input from '../Input';
import Loading from '../Loading';

import { optionsHorasAtendimento } from '../../utils/funcoes';
import getValidationErrors from '../../utils/getValidationErrors';
import getResponseErrors from '../../utils/getResponseErrors';
import { IAgendamentoListagem } from '../Agendamento';
import { PermissaoAdmin } from '../Agendamento/styles';

interface IProcedimento {
  ProCodigo: number;
  ProValor: number;
  ProNome: string;
}

interface IFormData {
  AgeConfCodigo: number;
  AgeData: Date;
  AgeHora: string;
  login?: string;
  senha?: string;
}

type ComponentProps = {
  AgeCodigo: number;
  PerCodigo: number;
  duracao: number;
  updated_at: Date;
  handleFechar(agendamentoListagem: IAgendamentoListagem | undefined): void;
};

const TransferirAgendamento: React.FC<ComponentProps> = ({
  AgeCodigo,
  PerCodigo,
  duracao,
  updated_at,
  handleFechar,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  // const [procedimentos, setProcedimentos] = useState<IProcedimento[]>();
  const [optionsAgendas, setOptionsAgendas] = useState<IOption[]>([]);
  const [optionsHora, _] = useState(() => optionsHorasAtendimento());
  const initialData = { AgeData: format(new Date(), 'yyyy-MM-dd') };
  const [exibePermissaoAdmin, setExibePermissaoAdmin] = useState(false);
  const [mensagemPermissaoAdmin, setMensagemPermissaoAdmin] = useState('');

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const responseAgendas = await api.get<IOption[]>(
          `agendas/porperfil/transferir?ConPerCodigo=${PerCodigo}`,
        );

        setOptionsAgendas(responseAgendas.data);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (PerCodigo) loadDados();
  }, [PerCodigo, addToast]);

  const handleSubmit = useCallback(
    async (dados: IFormData) => {
      console.log('teste');
      setLoading(true);
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeConfCodigo: Yup.number().required(),
          AgeData: Yup.date().required(),
          AgeHora: Yup.string().required('Hora obrigatório'),
          ...(exibePermissaoAdmin
            ? {
                login: Yup.string().required('Login é obrigatório'),
                senha: Yup.string().required('Senha é obrigatória'),
              }
            : {}),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataAgendamento = parseISO(
          `${dados.AgeData} ${dados.AgeHora}:00`,
        );

        const agendamento = {
          AgeConfCodigo: dados.AgeConfCodigo,
          AgeInicio: dataAgendamento,
          AgeFim: addMinutes(dataAgendamento, duracao),
          updated_at,
          ...(exibePermissaoAdmin
            ? { permissao: { UsuLogin: dados.login, UsuSenha: dados.senha } }
            : {}),
        };

        const appointment = await api.patch(
          `/agendamentos/${AgeCodigo}`,
          agendamento,
        );
        // pode pegar o retorno e jogar para a agenda
        addToast({ title: 'Agendamento salvo', type: 'success' });
        handleFechar(appointment.data);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        const statusErro = error?.response?.status;
        if (statusErro === 403) {
          setExibePermissaoAdmin(true);
          setMensagemPermissaoAdmin(mensagemErro);
        }
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar agendamento',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, handleFechar, exibePermissaoAdmin],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container className="my-4">
      {loading && <Loading />}

      <h5>Trensferir Agendamento</h5>
      <Form ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
        <Row className="mb-2">
          {exibePermissaoAdmin && (
            <PermissaoAdmin className="shadow border">
              <div>
                <h6>Permissão necessária</h6>
                <p>{mensagemPermissaoAdmin}</p>
                <Input name="login" label="Código ou Email" />
                <Input
                  name="senha"
                  label="Senha"
                  autoComplete="off"
                  type="password"
                />
                <Button
                  type="submit"
                  variant="primary mt-2"
                  size="sm"
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Confirmar'}
                </Button>

                <Button
                  type="button"
                  variant="outline-secondary mt-2 float-right"
                  size="sm"
                  disabled={loading}
                  onClick={() => {
                    setExibePermissaoAdmin(false);
                    setMensagemPermissaoAdmin('');
                  }}
                >
                  Voltar
                </Button>
              </div>
            </PermissaoAdmin>
          )}
          <Col sm={12}>
            <SelectSync
              name="AgeConfCodigo"
              label="Selecione a Agenda"
              // isClearable={false}
              placeholder="Selecione"
              handleSelectChange={handleOnChangeVazio}
              options={optionsAgendas}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Input name="AgeData" label="Data" type="date" />
          </Col>
          <Col md={4}>
            <SelectSync
              name="AgeHora"
              label="Hora"
              placeholder="Selecione"
              isClearable={false}
              handleSelectChange={handleOnChangeVazio}
              options={optionsHora}
            />
          </Col>
          <Col md={4}>
            <br />
            <Button type="submit" size="sm" className="mt-1" disabled={loading}>
              Transferir
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default TransferirAgendamento;
